import { useState, useRef, useEffect } from 'react'

interface VideoPreviewProps {
  src: string
  poster?: string
  className?: string
  onHover?: boolean
}

export function VideoPreview({ src, poster, className = '', onHover = true }: VideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [isVideoReady, setIsVideoReady] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleCanPlay = () => {
      setIsVideoReady(true)
    }

    const handleError = (e: Event) => {
      console.error('Video load error:', e)
    }

    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('error', handleError)

    return () => {
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('error', handleError)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !onHover || !isVideoReady) return

    if (isHovering) {
      video.play().catch((error) => {
        console.error('Video play error:', error)
      })
    } else {
      video.pause()
      video.currentTime = 0
    }
  }, [isHovering, onHover, isVideoReady])

  return (
    <div 
      className="relative w-full h-full"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Always show poster or placeholder */}
      {poster ? (
        <img
          src={poster}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isHovering && isVideoReady ? 'opacity-0' : 'opacity-100'
          }`}
        />
      ) : (
        // Fallback placeholder if no poster
        <div className="absolute inset-0 w-full h-full bg-gray-700" />
      )}
      
      {/* Video element - positioned absolutely */}
      <video
        ref={videoRef}
        src={src}
        disablePictureInPicture          // ← 停用子畫面按鈕
        className={`absolute inset-0 ${className} ${
          isHovering && isVideoReady ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-300`}
        loop
        muted
        playsInline
        preload="metadata"
      />
    </div>
  )
}